import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import executionService from '../../utils/executionService';
import campaignServiceEnhanced from '../../utils/campaignServiceEnhanced';

function CampaignDetails() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();

    const [campaign, setCampaign] = useState(null);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);

    // Poll for campaign status and leads every 5 seconds if active
    useEffect(() => {
        let interval;
        if (user?.id) {
            loadData();
            interval = setInterval(loadData, 5000);
        }
        return () => clearInterval(interval);
    }, [user?.id, id]);

    const loadData = async () => {
        try {
            const res = await campaignServiceEnhanced.getCampaigns(user.id);
            if (res.success) {
                const camp = res.data.find(c => c.id === id);
                if (camp) setCampaign(camp);
                else navigate('/campaigns');
            }

            const leadsRes = await campaignServiceEnhanced.getLeadsForCampaign(id, 100, 0);
            if (leadsRes.success) {
                setLeads(leadsRes.leads);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleStart = async () => {
        await executionService.startCampaignExecution(id, user.id);
        loadData();
    };

    const handlePause = async () => {
        await executionService.stopCampaignExecution(id, user.id);
        loadData();
    };

    if (loading && !campaign) {
        return (
            <DashboardLayout title="Campaign Details" currentPath={`/campaigns/${id}`}>
                <div className="flex items-center justify-center h-64">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
            </DashboardLayout>
        );
    }

    const isRunning = campaign?.campaign_status === 'active';
    const isFinished = campaign?.campaign_status === 'completed';

    return (
        <DashboardLayout title={campaign?.name || 'Campaign Details'} currentPath={`/campaigns/${id}`}>
            <div className="space-y-6">
                <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <button onClick={() => navigate('/campaigns')} className="text-muted-foreground hover:text-foreground">
                                <Icon name="ArrowLeft" size={24} />
                            </button>
                            <h1 className="text-3xl font-headline-bold text-foreground">{campaign?.name}</h1>
                            <div className={`px-3 py-1 rounded-full text-xs font-body-semibold ${isRunning ? 'bg-primary/20 text-primary animate-pulse' : isFinished ? 'bg-success/20 text-success' : 'bg-muted/20 text-muted-foreground'}`}>
                                {campaign?.campaign_status.toUpperCase()}
                            </div>
                        </div>
                        <p className="text-muted-foreground font-body">{campaign?.description || 'AI SDR Outreach Campaign'}</p>
                    </div>

                    <div className="flex items-center space-x-4">
                        {!isFinished && (
                            isRunning ? (
                                <Button onClick={handlePause} variant="outline" className="text-warning border-warning hover:bg-warning/10" iconName="Pause">
                                    Pause Execution
                                </Button>
                            ) : (
                                <Button onClick={handleStart} className="bg-primary hover:bg-primary/90 text-white" iconName="Play">
                                    Start SDR Engine
                                </Button>
                            )
                        )}
                    </div>
                </motion.div>

                {/* Dashboard Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[
                        { label: 'Total Leads', value: leads.length, color: 'text-foreground' },
                        { label: 'Messages Sent', value: leads.filter(l => l.message_sent).length, color: 'text-primary' },
                        { label: 'Replies', value: leads.filter(l => l.replied).length, color: 'text-success' },
                        { label: 'Meetings', value: leads.filter(l => l.meeting_scheduled).length, color: 'text-accent' }
                    ].map((stat, i) => (
                        <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.1 }} className="glassmorphism rounded-xl p-6 border border-border">
                            <p className="text-sm text-muted-foreground mb-2">{stat.label}</p>
                            <h3 className={`text-4xl font-headline-bold ${stat.color}`}>{stat.value}</h3>
                        </motion.div>
                    ))}
                </div>

                {/* Lead Queue */}
                <div className="glassmorphism rounded-xl p-6 border border-border mt-8">
                    <h2 className="text-xl font-headline-bold text-foreground mb-4">Lead Queue & Activity</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-border/50 text-sm font-body-semibold text-muted-foreground">
                                    <th className="pb-3 pr-4">Prospect</th>
                                    <th className="pb-3 px-4">Company</th>
                                    <th className="pb-3 px-4">Status</th>
                                    <th className="pb-3 px-4">Intent</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leads.map((lead, i) => (
                                    <tr key={lead.id} className="border-b border-border/20 hover:bg-card/50 transition-colors">
                                        <td className="py-4 pr-4">
                                            <p className="font-body-semibold text-foreground">{lead.full_name}</p>
                                            <p className="text-xs text-muted-foreground">{lead.job_title}</p>
                                        </td>
                                        <td className="py-4 px-4 text-sm text-muted-foreground">{lead.company}</td>
                                        <td className="py-4 px-4">
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${lead.replied ? 'bg-success/20 text-success' : lead.message_sent ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>
                                                {lead.replied ? 'Replied' : lead.message_sent ? 'Contacted' : 'Pending'}
                                            </span>
                                        </td>
                                        <td className="py-4 px-4 text-sm">
                                            {lead.reply_intent ? (
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${lead.reply_intent === 'interested' ? 'bg-success/20 text-success' : lead.reply_intent === 'not_interested' ? 'bg-error/20 text-error' : 'bg-warning/20 text-warning'}`}>
                                                    {lead.reply_intent.toUpperCase()}
                                                </span>
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {leads.length === 0 && <p className="text-center text-muted-foreground mt-4 py-8">No leads imported yet.</p>}
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}

export default CampaignDetails;
